import { GameServices } from "game/GameServices";
import { View } from "@westpierstudio/luma/mvc";
import { Orientation } from "@westpierstudio/luma/layout";
import { Tween } from "@westpierstudio/luma/createjs";
import { Signal } from "type-signals";
import { AdvancedText } from "@westpierstudio/luma/utils/advancedtext";
import { Text } from "pixi.js";
import { BoundedContainer, ImplementBoundedContainer } from "utils/BoundedContainer";

class FreeSpinCounterSignals {
    totalCounterIncrement = new Signal();
    totalCountUpComplete = new Signal();
}

export class FreeSpinCounter extends View {
    private spec: Record<string, any>;
    private headerText!: Text | AdvancedText;
    private ofText!: Text | AdvancedText;
    private headerContainer!: BoundedContainer;
    private countContainer!: BoundedContainer;
    private currentValue!: Text | AdvancedText;
    private totalValue!: Text | AdvancedText;
    signals = new FreeSpinCounterSignals();

    constructor(services: GameServices) {
        super(services);

        const {factory, resources} = this.services;

        this.spec = resources.getSpec(`freespincounter`);

        factory.createSprite(this.spec.panel, this);

        this.headerText = factory.createText(resources.getMessage(`freeSpinCounterHeader`), this.spec.freeSpinCounterHeader, this);
        this.headerContainer = ImplementBoundedContainer(this.headerText, this.spec.maxBounds.header);
        this.headerContainer.update();

        this.ofText = factory.createText(resources.getMessage(`freeSpinCounterOf`), this.spec.freeSpinCounterOf, this);
        this.countContainer = ImplementBoundedContainer(this.ofText, this.spec.maxBounds.count);

        this.currentValue = factory.createText(`0`, this.spec.freeSpinCounterCurrent, this.countContainer);
        this.totalValue = factory.createText(`0`, this.spec.freeSpinCounterTotal, this.countContainer);
        this.countContainer.update();

        this.visible = false;
    }

    setFreeSpins(remaining: number, total: number): void {
        this.currentValue.text = this.services.resources.getMessage(`freeSpinCounterCurrent`, {current: `${total - remaining}`});
        this.totalValue.text = this.services.resources.getMessage(`freeSpinCounterTotal`, {total: `${total}`});

        this.countContainer.update();
    }

    countUpToTotalFreeSpins(remaining: number, total: number): void {

        const {resources} = this.services;

        const countUpPerDigitDelay = resources.getSetting(`freeSpinCounter`).countUpPerDigitDelay;
        this.currentValue.text = resources.getMessage(`freeSpinCounterCurrent`, {current: `${total - remaining}`});

        const currentFreeSpinTotal = this.totalValue.text === `` ? 0 : parseInt(this.totalValue.text);
        const tween = Tween.get(this);

        for (let i=currentFreeSpinTotal; i<=total; i++) {

            tween.call(() => {
                this.totalValue.text = this.services.resources.getMessage(`freeSpinCounterTotal`, {total: `${i}`});
                this.signals.totalCounterIncrement.dispatch();
            });

            if (i === total) {
                tween.call(() => this.signals.totalCountUpComplete.dispatch());
            } else {
                tween.wait(countUpPerDigitDelay);
            }
        }

        this.countContainer.update();
    }

    skip(total: number): void {

        Tween.removeTweens(this);

        this.totalValue.text = this.services.resources.getMessage(`freeSpinCounterTotal`, {total: `${total}`});
        this.signals.totalCountUpComplete.dispatch();
    }

    resize(orientation: Orientation): void {

        const {factory} = this.services;

        if (orientation === `landscape`) {
            factory.applySpec(this, this.spec.freeSpinCounterLandscape);
        } else {
            factory.applySpec(this, this.spec.freeSpinCounterPortrait);
        }
    }

    appear(): void {

        Tween.removeTweens(this);
        this.alpha = 1;
        this.visible = true;
    }

    fadeOut(): void {

        const fadeTime = this.services.resources.getSetting(`freeSpinsCounterFadeTime`);

        Tween.get(this).to({alpha: 0}, fadeTime)
        .call(() => {
            this.visible = false;
        });
    }

    override reset(): void {

        Tween.removeTweens(this);

        this.currentValue.text = `0`;
        this.totalValue.text = `0`;

        this.visible = false;

        this.countContainer.update();
    }
}